import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import { hasPermission, useSession } from "../auth/session";
import { utc } from "../import/items";

const KEY = ["checkpoints"] as const;

/**
 * Integrity checkpoints (threat model §3.3): administrators take them and set
 * the interval; the custodian downloads them for write-once media.
 */
export function IntegrityPage() {
  const qc = useQueryClient();
  const session = useSession();
  const admin = !!session.data && hasPermission(session.data, "ManageSystem");
  const status = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/checkpoints")),
  });
  const [hours, setHours] = useState("");
  const [from, setFrom] = useState("");
  const refresh = () => void qc.invalidateQueries({ queryKey: KEY });
  const take = useMutation({
    mutationFn: () => unwrap(api.POST("/api/checkpoints")),
    onSuccess: refresh,
  });
  const save = useMutation({
    mutationFn: (h: number) =>
      unwrap(
        api.PUT("/api/checkpoints/settings", { body: { interval_hours: h } }),
      ),
    onSuccess: () => {
      setHours("");
      refresh();
    },
  });

  if (status.isError)
    return <Alert severity="error">{status.error.message}</Alert>;
  const s = status.data;
  if (!s) return null;
  const newest = s.checkpoints[0]?.number;
  const first = Number(from) || 1;

  return (
    <Stack spacing={3} component="section" aria-label="Integrity checkpoints">
      <Typography variant="h5" component="h2">
        Integrity checkpoints
      </Typography>
      <Typography color="text.secondary">
        Each checkpoint signs the head of the audit log and every recording's
        record (hash, provenance, timing and cases), linked to the one before.
        The custodian keeps them on write-once media, such as a finalised DVD-R,
        and checks them with avgv-verify: changes made outside AVGV then show
        up.
      </Typography>
      {s.late && (
        <Alert severity="error">
          A checkpoint was due at {utc(s.due_at).slice(0, 16)} UTC and hasn't
          been taken. Check the signing key, then take one now.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography>
            Every {s.interval_hours} h; next due{" "}
            <span aria-label="Next due">{utc(s.due_at).slice(0, 16)}</span> UTC.
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            Signed with a {s.signing_key.kind}, fingerprint{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontFamily: "monospace" }}
              aria-label="Signing key fingerprint"
            >
              {s.signing_key.fingerprint}
            </Typography>
          </Typography>
          {admin && (
            <Stack
              direction="row"
              sx={{ gap: 1, alignItems: "center", flexWrap: "wrap", pt: 1 }}
            >
              <Button
                variant="contained"
                disabled={take.isPending}
                onClick={() => take.mutate()}
              >
                Take checkpoint now
              </Button>
              <TextField
                size="small"
                type="number"
                label="Interval (hours)"
                value={hours}
                placeholder={String(s.interval_hours)}
                onChange={(e) => setHours(e.target.value)}
                slotProps={{
                  htmlInput: { min: 1, max: s.max_interval_hours },
                }}
                helperText={`At most ${s.max_interval_hours} h; changes are audited and shown to the custodian`}
              />
              <Button
                disabled={!hours || save.isPending}
                onClick={() => save.mutate(Number(hours))}
              >
                Save interval
              </Button>
            </Stack>
          )}
          {take.isError && <Alert severity="error">{take.error.message}</Alert>}
          {save.isError && <Alert severity="error">{save.error.message}</Alert>}
        </Stack>
      </Paper>
      {newest !== undefined && (
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            type="number"
            label="From checkpoint"
            sx={{ width: 160 }}
            value={from}
            placeholder="1"
            onChange={(e) => setFrom(e.target.value)}
            slotProps={{ htmlInput: { min: 1, max: newest } }}
          />
          <Button
            variant="outlined"
            href={`/api/checkpoints/package?from=${first}`}
          >
            Download {first}–{newest}
          </Button>
        </Stack>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Checkpoints">
          <TableHead>
            <TableRow>
              <TableCell>No.</TableCell>
              <TableCell>Taken (UTC)</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Audit entries</TableCell>
              <TableCell>Records</TableCell>
              <TableCell>Merkle root</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {s.checkpoints.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>None yet.</TableCell>
              </TableRow>
            )}
            {s.checkpoints.map((c) => (
              <TableRow key={c.number}>
                <TableCell>{c.number}</TableCell>
                <TableCell
                  sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
                >
                  {utc(c.created_at).slice(0, 19)}
                </TableCell>
                <TableCell>{c.taken_by ?? "schedule"}</TableCell>
                <TableCell>{c.audit_seq ?? 0}</TableCell>
                <TableCell>{c.records}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {c.merkle_root.slice(0, 16)}…
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
